import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, CheckCircle2, XCircle, Loader2, MapPin, Wrench, Calendar, Download } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

/**
 * Publieke verificatie-pagina voor een onderhoudscertificaat.
 * Bereikbaar via de QR-code op de PDF. Vereist géén login.
 * URL: /verify/:certNr
 */
export default function VerifyCertificaat() {
  const { certNr } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found | error

  useEffect(() => {
    const base = BACKEND_URL || "";
    axios
      .get(`${base}/api/onderhoudscertificaten/verify/${encodeURIComponent(certNr)}`)
      .then((r) => {
        setData(r.data);
        setStatus("ok");
      })
      .catch((e) => {
        if (e?.response?.status === 404) setStatus("not_found");
        else setStatus("error");
      });
  }, [certNr]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4"
      data-testid="verify-page"
    >
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#0D3B66] flex items-center justify-center">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#F26522] font-bold">
              Certificaat verificatie
            </div>
            <div className="text-2xl font-bold text-slate-900">Brandpreventie Techniek B.V.</div>
          </div>
        </div>

        {/* Loading */}
        {status === "loading" && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-slate-400 mb-3" />
            <div className="text-slate-500">Certificaat controleren…</div>
          </div>
        )}

        {/* Not found */}
        {status === "not_found" && (
          <div
            className="bg-white rounded-xl shadow-md p-8 border-l-4 border-red-500"
            data-testid="verify-notfound"
          >
            <div className="flex items-start gap-3">
              <XCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div>
                <div className="text-lg font-bold text-red-700">Certificaat niet gevonden</div>
                <div className="text-sm text-slate-600 mt-1">
                  Certificaatnummer <span className="font-mono font-semibold">{certNr}</span> is niet
                  bekend in onze administratie. Neem contact op via{" "}
                  <a
                    className="text-[#F26522] underline"
                    href="mailto:contact@brandpreventietechniek.nl"
                  >
                    contact@brandpreventietechniek.nl
                  </a>{" "}
                  als u een certificaat heeft dat wel echt zou moeten zijn.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {status === "error" && (
          <div className="bg-white rounded-xl shadow-md p-8 border-l-4 border-amber-500">
            <div className="text-amber-700 font-semibold">Verificatie tijdelijk onbeschikbaar</div>
            <div className="text-sm text-slate-600 mt-1">
              Probeer het over een minuut nog eens of neem contact op via 055-203 22 93.
            </div>
          </div>
        )}

        {/* Success */}
        {status === "ok" && data && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden" data-testid="verify-ok">
            {/* Groene "geldig" balk */}
            <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-4 flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 flex-shrink-0" />
              <div>
                <div className="text-emerald-700 font-bold">Certificaat is geldig</div>
                <div className="text-xs text-slate-600">
                  Uitgegeven door Brandpreventie Techniek B.V.
                </div>
              </div>
            </div>

            {/* Kern-details */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#F26522] font-bold">
                  Certificaatnummer
                </div>
                <div className="text-lg font-bold text-slate-900 font-mono">{data.cert_nr}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#F26522] font-bold">
                  Werkbonnummer
                </div>
                <div className="text-lg font-bold text-slate-900 font-mono">
                  {data.werkbonnummer}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#F26522] font-bold">
                  Uitgegeven aan
                </div>
                <div className="text-slate-900 font-semibold">{data.klantnaam}</div>
                {data.stad && data.stad !== "—" && (
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3" /> {data.stad}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#F26522] font-bold">
                  Uitvoerdatum
                </div>
                <div className="text-slate-900 font-semibold flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  {data.uitvoerdatum || "—"}
                </div>
                {data.monteur && data.monteur !== "—" && (
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <Wrench className="h-3 w-3" /> Monteur: {data.monteur}
                  </div>
                )}
              </div>
            </div>

            {/* Onderhouden materialen */}
            <div className="border-t border-slate-100 px-6 py-4">
              <div className="text-[10px] uppercase tracking-wider text-[#F26522] font-bold mb-3">
                Onderhouden materialen conform NEN-normen
              </div>
              {(data.regels || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic">
                  Geen certificaat-plichtige regels geregistreerd.
                </div>
              ) : (
                <div className="overflow-hidden border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0D3B66] text-white">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold"></th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Aantal</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Type</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Norm</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.regels.map((r, i) => (
                        <tr
                          key={i}
                          className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
                          data-testid={`verify-regel-${i}`}
                        >
                          <td className="px-3 py-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          </td>
                          <td className="px-3 py-2 font-bold">{r.aantal}×</td>
                          <td className="px-3 py-2 font-semibold">{r.categorie}</td>
                          <td className="px-3 py-2 text-slate-600">{r.norm}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Verklaring */}
            <div className="border-t border-slate-100 px-6 py-4 bg-slate-50">
              <div className="text-xs text-slate-600 leading-relaxed">{data.verklaring}</div>
            </div>

            {/* Download PDF-knop */}
            <div className="border-t border-slate-100 px-6 py-4 flex justify-center">
              <a
                href={`${BACKEND_URL || ""}/api/onderhoudscertificaten/verify/${encodeURIComponent(data.cert_nr)}/download`}
                className="inline-flex items-center gap-2 bg-[#0D3B66] hover:bg-[#0a2f52] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition shadow-md hover:shadow-lg"
                data-testid="verify-download-btn"
              >
                <Download className="h-4 w-4" />
                Download certificaat (PDF)
              </a>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-6 py-3 text-[11px] text-slate-500 text-center">
              contact@brandpreventietechniek.nl · 055-203 22 93 · brandpreventietechniek.nl
              <div className="mt-1">
                Uitgegeven op:{" "}
                {(data.uitgifte_datum || "").slice(0, 10) || "—"}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-[10px] text-slate-400">
          Deze pagina is publiek toegankelijk voor verificatie via de QR-code op het certificaat.
        </div>
      </div>
    </div>
  );
}
